import React, { useState } from 'react'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import IconButton from '@mui/material/IconButton'
import Collapse from '@mui/material/Collapse'
import Grid from '@mui/material/Grid'
import Divider from '@mui/material/Divider'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import CancelIcon from '@mui/icons-material/Cancel'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import EventAvailableIcon from '@mui/icons-material/EventAvailable'
import GroupIcon from '@mui/icons-material/Group'
import StarIcon from '@mui/icons-material/Star'
import AttachMoneyIcon from '@mui/icons-material/AttachMoney'
import PercentIcon from '@mui/icons-material/Percent'

const LegendItem = ({ icon, title, description, color }) => (
  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 40,
        height: 40,
        borderRadius: '8px',
        backgroundColor: color ? `${color}15` : '#f3f4f6',
        color: color || '#666',
        flexShrink: 0,
      }}
    >
      {icon}
    </Box>
    <Box sx={{ flex: 1 }}>
      <Typography
        variant="subtitle2"
        sx={{ fontWeight: 600, color: '#333', mb: 0.5 }}
      >
        {title}
      </Typography>
      <Typography
        variant="body2"
        sx={{ color: '#666', fontSize: '13px', lineHeight: 1.5 }}
      >
        {description}
      </Typography>
    </Box>
  </Box>
)

const DashboardLegend = () => {
  const [expanded, setExpanded] = useState(false)

  const metrics = [
    {
      icon: <GroupIcon fontSize="small" />,
      title: 'Bookings',
      description:
        'Total number of appointments scheduled within the selected date range',
      color: '#0074FE',
    },
    {
      icon: <EventAvailableIcon fontSize="small" />,
      title: 'Showed Up',
      description: 'Patients who attended their scheduled appointments',
      color: '#05AD48',
    },
    {
      icon: <CancelIcon fontSize="small" />,
      title: 'No-Shows',
      description:
        'Patients who missed their appointments without prior notice',
      color: '#FC7777',
    },
    {
      icon: <StarIcon fontSize="small" />,
      title: 'Starts',
      description:
        'Patients who began their treatment plan (converted to paying customers)',
      color: '#FFA500',
    },
    {
      icon: <PercentIcon fontSize="small" />,
      title: 'Show Rate',
      description:
        'Percentage of scheduled patients who showed up (Showed Up ÷ Bookings × 100)',
      color: '#8B5CF6',
    },
    {
      icon: <TrendingUpIcon fontSize="small" />,
      title: 'Conversion Rate',
      description:
        'Percentage of bookings that converted to treatment starts (Starts ÷ Bookings × 100)',
      color: '#06B6D4',
    },
    {
      icon: <AttachMoneyIcon fontSize="small" />,
      title: 'Revenue',
      description: 'Total revenue generated from treatment starts',
      color: '#05AD48',
    },
  ]

  const utmDefinitions = [
    {
      title: 'UTM Source',
      description:
        'The referrer or platform that sent traffic (e.g., google, facebook, newsletter)',
    },
    {
      title: 'UTM Campaign',
      description:
        'The specific campaign name for tracking (e.g., summer_promo, black_friday)',
    },
    {
      title: 'UTM Medium',
      description: 'The marketing medium (e.g., cpc, email, social)',
    },
    {
      title: 'New Patients',
      description: 'First-time patients from a specific marketing source',
    },
    {
      title: 'Future Appointments',
      description: 'Appointments scheduled for dates beyond the current period',
    },
  ]

  const colorCodes = [
    {
      label: 'Green',
      color: '#05AD48',
      description: 'Good performance (80%+ show rate, 30%+ conversion)',
    },
    {
      label: 'Yellow',
      color: '#FFA500',
      description: 'Average performance (60-79% show rate, 15-29% conversion)',
    },
    {
      label: 'Red',
      color: '#FC7777',
      description: 'Needs improvement (<60% show rate, <15% conversion)',
    },
  ]

  return (
    <Paper
      elevation={0}
      sx={{
        mt: 4,
        p: 3,
        borderRadius: 2,
        border: '1px solid #e0e0e0',
        backgroundColor: '#fafbfc',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: expanded ? 3 : 0,
          cursor: 'pointer',
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <InfoOutlinedIcon sx={{ color: '#0074FE', fontSize: 24 }} />
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#333' }}>
            Dashboard Definitions
          </Typography>
        </Box>
        <IconButton size="small" sx={{ color: '#666' }}>
          {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>

      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 600, color: '#333', mb: 2 }}
            >
              Key Metrics
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {metrics.map((metric, index) => (
                <LegendItem key={index} {...metric} />
              ))}
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 600, color: '#333', mb: 2 }}
            >
              UTM & Marketing Terms
            </Typography>
            <Box
              sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}
            >
              {utmDefinitions.map((def, index) => (
                <Box key={index}>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 600, color: '#333', mb: 0.5 }}
                  >
                    {def.title}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: '#666', fontSize: '13px' }}
                  >
                    {def.description}
                  </Typography>
                </Box>
              ))}
            </Box>

            <Divider sx={{ my: 2 }} />

            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 600, color: '#333', mb: 2 }}
            >
              Performance Color Codes
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {colorCodes.map((code, index) => (
                <Box
                  key={index}
                  sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}
                >
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      borderRadius: '50%',
                      backgroundColor: code.color,
                      flexShrink: 0,
                    }}
                  />
                  <Typography
                    variant="body2"
                    sx={{ color: '#666', fontSize: '13px' }}
                  >
                    <strong>{code.label}:</strong> {code.description}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ backgroundColor: '#f0f7ff', p: 2, borderRadius: 1 }}>
          <Typography
            variant="body2"
            sx={{ color: '#0074FE', fontWeight: 500, mb: 1 }}
          >
            Tips for Using This Dashboard
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: '#666', fontSize: '13px', lineHeight: 1.6 }}
          >
            • Use date range presets for quick analysis of different time
            periods
            <br />
            • Click on table headers to view tooltips explaining each metric
            <br />
            • Export data to CSV for further analysis in spreadsheet
            applications
            <br />
            • Monitor conversion rates to identify high-performing marketing
            channels
            <br />• Track show rates to optimize appointment scheduling and
            reduce no-shows
          </Typography>
        </Box>
      </Collapse>
    </Paper>
  )
}

export default DashboardLegend
